import type { ContextLink } from "@bindery/shared/corpus/document";
import type { Chip } from "@bindery/shared/reader/gate";
import type { CooldownPhase } from "@bindery/ui/hooks/use-quiz-cooldown";

export interface ReferenceSelection {
  readonly text: string;
  readonly language: string;
  readonly singleWord: boolean;
  readonly confounders: readonly string[];
  readonly context: ContextLink | null;
}

export interface DictionaryEntry {
  readonly id: string;
  readonly headword: string;
  readonly partOfSpeech: string | null;
  readonly senses: readonly string[];
  readonly source: string;
  readonly sourceUrl: string | null;
}

export interface ReferencePronunciation {
  readonly id: string;
  readonly label: string;
  readonly language: string;
  readonly respelling: string | null;
  readonly ipa: string | null;
  readonly audioUrl: string | null;
  readonly source: "curated" | "dictionary" | "synthesized";
}

export type LookupResource<T> =
  | { readonly status: "idle" | "loading" | "missing" | "error" }
  | { readonly status: "ready"; readonly value: T };

export interface ReferenceLookupPorts {
  readonly dictionary: (
    selection: ReferenceSelection,
    signal: AbortSignal,
  ) => Promise<readonly DictionaryEntry[]>;
  readonly pronunciations: (
    selection: ReferenceSelection,
    signal: AbortSignal,
  ) => Promise<readonly ReferencePronunciation[]>;
  /** Resolves on playback completion; abort must stop both pending and active audio. */
  readonly play: (
    pronunciation: ReferencePronunciation,
    signal: AbortSignal,
  ) => Promise<void>;
}

export type ReferenceAnswer =
  | { readonly kind: "context"; readonly link: ContextLink }
  | {
      readonly kind: "dictionary";
      readonly entries: LookupResource<readonly DictionaryEntry[]>;
    };

export type ReferenceGate =
  | { readonly status: "open" }
  | {
      readonly status: "matching";
      readonly choices: readonly Chip[];
      readonly selectedId: number | null;
      readonly wrongCount: number;
      readonly phase: CooldownPhase;
    };

export type ReferenceLookupState =
  | { readonly status: "closed"; readonly autoplay: boolean }
  | {
      readonly status: "open";
      readonly autoplay: boolean;
      readonly selection: ReferenceSelection;
      readonly gate: ReferenceGate;
      readonly answer: ReferenceAnswer | null;
      readonly pronunciations: LookupResource<
        readonly ReferencePronunciation[]
      >;
      readonly playback: "idle" | "playing" | "error";
      readonly playingId: string | null;
    };

export function preferredPronunciation(
  pronunciations: readonly ReferencePronunciation[],
): ReferencePronunciation | undefined {
  return (
    pronunciations.find((p) => p.source === "curated" && p.audioUrl !== null) ??
    pronunciations.find(
      (p) => p.source === "dictionary" && p.audioUrl !== null,
    ) ??
    pronunciations.find((p) => p.audioUrl !== null)
  );
}
